import { useState, useRef } from 'react';
import StatusBadge from './StatusBadge';
import { ideasApi } from '../../lib/api';

const STATUSES = ['concept', 'prototype', 'pitched', 'licensed', 'archived', 'sleeping'];

const PROFILE_LABELS = {
  product_category: 'Category',
  target_age_range: 'Age Range',
  mechanism_type: 'Play Mechanic',
  estimated_cost_tier: 'Cost Tier',
};

export default function IdeaDetail({ idea, onBack, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(idea.title);
  const [description, setDescription] = useState(idea.description || '');
  const [status, setStatus] = useState(idea.status);
  const [saving, setSaving] = useState(false);
  const [profiling, setProfiling] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileRef = useRef();

  const profile = idea.idea_profiles?.[0] || idea.idea_profiles;
  const assets = idea.idea_assets || [];

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(idea.id, { title, description, status });
      setEditing(false);
    } catch (err) {
      console.error(err);
    }
    setSaving(false);
  };

  const handleProfile = async () => {
    setProfiling(true);
    try {
      await ideasApi.profile(idea.id);
      await onUpdate(idea.id, {}); // refresh
    } catch (err) {
      console.error(err);
    }
    setProfiling(false);
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        await ideasApi.uploadAsset(idea.id, file);
      }
      await onUpdate(idea.id, {}); // refresh
    } catch (err) {
      console.error(err);
    }
    setUploading(false);
  };

  const handleDeleteAsset = async (assetId) => {
    try {
      await ideasApi.deleteAsset(idea.id, assetId);
      await onUpdate(idea.id, {}); // refresh
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this idea? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await onDelete(idea.id);
    } catch (err) {
      console.error(err);
    }
    setDeleting(false);
  };

  return (
    <div>
      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-vault-navy mb-4 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Vault
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Header */}
          <div className="bg-white rounded-xl border border-vault-border p-6">
            <div className="flex items-start justify-between mb-4">
              {editing ? (
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="text-2xl font-bold text-vault-navy w-full px-2 py-1 border border-vault-border rounded-lg focus:outline-none focus:ring-2 focus:ring-vault-gold"
                />
              ) : (
                <h1 className="text-2xl font-bold text-vault-navy">{idea.title}</h1>
              )}
              <StatusBadge status={editing ? status : idea.status} />
            </div>

            {editing ? (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-500 mb-1">Status</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="px-3 py-2 border border-vault-border rounded-lg focus:outline-none focus:ring-2 focus:ring-vault-gold bg-white"
                  >
                    {STATUSES.map(s => (
                      <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={8}
                  className="w-full px-3 py-2 border border-vault-border rounded-lg focus:outline-none focus:ring-2 focus:ring-vault-gold resize-none"
                  placeholder="Describe your idea..."
                />
                <div className="flex gap-3 mt-4">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 bg-vault-gold hover:bg-vault-gold-dark text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    onClick={() => { setEditing(false); setTitle(idea.title); setDescription(idea.description || ''); setStatus(idea.status); }}
                    className="px-4 py-2 border border-vault-border text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-gray-600 whitespace-pre-wrap">{idea.description || 'No description yet.'}</p>
                <div className="flex gap-3 mt-4">
                  <button
                    onClick={() => setEditing(true)}
                    className="px-4 py-2 bg-vault-gold hover:bg-vault-gold-dark text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="px-4 py-2 border border-vault-error/30 text-vault-error text-sm font-medium rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                  >
                    {deleting ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Assets */}
          <div className="bg-white rounded-xl border border-vault-border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-vault-navy">Files & Assets</h2>
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="px-3 py-1.5 bg-vault-gold hover:bg-vault-gold-dark text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
              <input
                ref={fileRef}
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx,.txt,.stl,.obj,.fbx"
                onChange={handleUpload}
                className="hidden"
              />
            </div>

            {assets.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <p className="text-sm">No files uploaded yet</p>
                <p className="text-xs mt-1">Upload images, PDFs, sketches, or 3D files</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {assets.map(asset => (
                  <div key={asset.id} className="relative group">
                    {asset.file_type?.startsWith('image/') ? (
                      <img
                        src={asset.file_url}
                        alt={asset.file_name}
                        className="w-full h-28 object-cover rounded-lg"
                      />
                    ) : (
                      <div className="w-full h-28 bg-vault-surface rounded-lg flex flex-col items-center justify-center">
                        <span className="text-2xl mb-1">&#128196;</span>
                        <span className="text-xs text-gray-500 truncate px-2 max-w-full">{asset.file_name}</span>
                      </div>
                    )}
                    <button
                      onClick={() => handleDeleteAsset(asset.id)}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar — AI Profile */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-vault-border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-vault-navy">AI Profile</h2>
              <button
                onClick={handleProfile}
                disabled={profiling}
                className="text-xs px-2.5 py-1 bg-vault-navy text-white rounded-lg hover:bg-vault-slate transition-colors disabled:opacity-50"
              >
                {profiling ? 'Analyzing...' : profile ? 'Re-analyze' : 'Analyze'}
              </button>
            </div>

            {profile ? (
              <div className="space-y-3">
                {Object.entries(PROFILE_LABELS).map(([key, label]) => (
                  profile[key] && (
                    <div key={key}>
                      <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">{label}</div>
                      <div className="text-sm text-vault-navy font-medium mt-0.5 capitalize">
                        {profile[key].replace(/_/g, ' ')}
                      </div>
                    </div>
                  )
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-gray-400">
                <p className="text-sm">No AI profile yet</p>
                <p className="text-xs mt-1">Add a description and click Analyze</p>
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="bg-white rounded-xl border border-vault-border p-6">
            <h2 className="text-lg font-semibold text-vault-navy mb-4">Details</h2>
            <div className="space-y-3 text-sm">
              <div>
                <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Created</div>
                <div className="text-vault-navy">{new Date(idea.created_at).toLocaleDateString()}</div>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Last Updated</div>
                <div className="text-vault-navy">{new Date(idea.updated_at).toLocaleDateString()}</div>
              </div>
              {idea.tags?.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Tags</div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {idea.tags.map(tag => (
                      <span key={tag} className="text-xs px-2 py-0.5 bg-vault-surface rounded-full text-gray-600">{tag}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
